import React from "react";

type ButtonVariant = "fill" | "outline" | "text";
type ButtonColor = "primary" | "secondary";
type ButtonSize = "small" | "default" | "large" | "xlarge";

interface StandardButtonProps {
  text: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  width?: string | number;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  type?: "button" | "submit" | "reset";
}

const colorMap = {
  primary: "#24AEA8",
  secondary: "#171C27",
  disabled: "#63615C",
};

const StandardButton: React.FC<StandardButtonProps> = ({
  text,
  startIcon,
  endIcon,
  variant = "fill",
  color = "primary",
  size = "default",
  width,
  disabled = false,
  onClick,
  className = "",
  type = "button",
}) => {
  const activeColor = disabled ? colorMap.disabled : colorMap[color];

  const isText = variant === "text";
  const isSmall = size === "small";

  const getPadding = () => {
    if (isText) return "4px 8px";
    if (isSmall) return "4px 8px";
    if (size === "large") return "14px 28px";
    if (size === "xlarge") return "16px 32px";
    return "12px 24px";
  };

  const getHeight = () => {
    if (isText) return "auto";
    if (isSmall) return "32px";
    if (size === "large") return "52px";
    if (size === "xlarge") return "56px";
    return "48px";
  };

  const textColor = variant === "fill" ? "#FFFFFF" : activeColor;

  const baseStyles: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: isSmall ? "4px" : "8px",
    padding: getPadding(),
    borderRadius: isText ? "4px" : "8px",
    fontFamily: "Interstate, sans-serif",
    fontSize: "14px",
    fontWeight: isText ? 600 : 500,
    lineHeight: "22px",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    border: variant === "outline" ? `1px solid ${activeColor}` : "none",
    backgroundColor: variant === "fill" ? activeColor : "transparent",
    color: textColor,
    height: getHeight(),
    width: width ?? undefined,
    whiteSpace: "nowrap",
  };

  const renderIcon = (icon: React.ReactNode) => {
    if (!icon) return null;
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement<{ color?: string }>, {
        color: textColor,
      });
    }
    return icon;
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={baseStyles}
      className={className}
    >
      {startIcon && <span className="flex items-center">{renderIcon(startIcon)}</span>}
      <span>{text}</span>
      {endIcon && <span className="flex items-center">{renderIcon(endIcon)}</span>}
    </button>
  );
};

export default StandardButton;
